import { Box, type SxProps } from "@mui/material";
import { ActionButton, CommonImageComp } from "../../components/ui";
import { comparisonImageStyle } from "../../components/ui/CommonImageComp/styles";
import { useThemeContext } from "../../globalContext/global-context";
import type { QuestionViewProps } from "../contracts";
import {
  compactGridButtonStyle,
  compactGridCardStyle,
  compactGridListStyle,
} from "../grid/gridInstrumentStyle";
import type { MaxDiffDefinition, MaxDiffDraft, MaxDiffEvent } from "./core";
import { questionWrapper, textWrapper } from "./styles";

type MaxDiffViewProps = QuestionViewProps<
  MaxDiffDefinition,
  MaxDiffDraft,
  MaxDiffEvent
>;

export const MaxDiffView = (props: MaxDiffViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const { definition, draft } = props;
  const compact = props.display.arrangement === "compact";

  const choice = (
    row: MaxDiffDefinition["rows"][number],
    column: MaxDiffDefinition["options"][number],
    index: number,
  ) => {
    const selected = draft.selected[column.id] === row.id;
    const other = column.id === "left" ? "right" : "left";
    const disabled =
      !props.controls.canSubmit ||
      column.disabled ||
      (draft.selected[column.id] !== null && !selected) ||
      draft.selected[other] === row.id;
    return (
      <ActionButton
        key={column.id}
        ariaLabel={`${column.label}: ${row.label}`}
        ariaPressed={selected}
        disabled={disabled}
        isSelected={selected}
        onClick={() =>
          props.dispatch({ type: "select", column: column.id, rowId: row.id })
        }
        testId={`LeftRight-${index + 1}-${column.id === "left" ? 1 : 2}`}
        style={{ ...compactGridButtonStyle(theme, selected), maxWidth: "none" }}
      >
        {column.label}
      </ActionButton>
    );
  };

  return (
    <Box data-testid="max-diff-view" sx={{ width: "100%" }}>
      <Box
        sx={
          compact
            ? compactGridListStyle(theme)
            : ([
                questionWrapper(theme),
                { width: "100%", maxWidth: "100%" },
              ] as SxProps)
        }
      >
        {definition.rows.map((row, index) => (
          <Box
            key={row.id}
            sx={{
              ...(compact ? compactGridCardStyle(theme) : {}),
              display: "grid",
              gridTemplateColumns: compact
                ? "minmax(0, 1fr) minmax(0, 1fr)"
                : "minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr)",
              gap: compact ? `${theme.tokens.spacing["1"]}px` : undefined,
              alignItems: "stretch",
            }}
          >
            {compact ? (
              <>
                <Box
                  sx={
                    [
                      textWrapper(theme.tokens.color.action.primary),
                      { gridColumn: "1 / -1" },
                    ] as SxProps
                  }
                >
                  <CommonImageComp
                    currentVal={{
                      image: row.image,
                      text: row.label,
                      display_text: row.displayLabel,
                      alt_text: row.label,
                    }}
                    imageStyle={comparisonImageStyle}
                  />
                </Box>
                {definition.options.map((column) => choice(row, column, index))}
              </>
            ) : (
              <>
                {choice(row, definition.options[0], index)}
                <Box sx={textWrapper(theme.tokens.color.action.primary)}>
                  <CommonImageComp
                    currentVal={{
                      image: row.image,
                      text: row.label,
                      display_text: row.displayLabel,
                      alt_text: row.label,
                    }}
                    imageStyle={comparisonImageStyle}
                    adaptiveText={{ maxLines: 3 }}
                  />
                </Box>
                {choice(row, definition.options[1], index)}
              </>
            )}
          </Box>
        ))}
      </Box>
      {definition.dontKnow && (
        <ActionButton
          currentVal={{ text: definition.dontKnow.label, display_text: true }}
          ariaLabel={definition.dontKnow.label}
          disabled={!props.controls.canSubmit}
          isSelected={draft.dontKnow}
          onClick={() => props.submit({ type: "dont-know" })}
          testId="max-diff-dont-know"
        />
      )}
    </Box>
  );
};
